import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyIcon,
  EmptyIconProps,
  EmptyMedia,
  EmptyTitle,
} from '@sr/ui-library';
import { ComponentProps } from 'react';
import { SUPPORT_EMAIL } from '../constant/Common';
import AddTitleLink from './AddTitleLink';

export interface MSR3EmptyProps {
  children?: ComponentProps<typeof EmptyContent>['children'];
  className?: string;
  description?: ComponentProps<typeof EmptyDescription>['children'];
  iconProps?: EmptyIconProps;
  title?: ComponentProps<typeof EmptyTitle>['children'];
}

export type MSR3EmptyContentType = 'client' | 'no-result' | 'payment' | 'title';

type MSR3EmptyContent = Pick<
  MSR3EmptyProps,
  'children' | 'description' | 'iconProps' | 'title'
>;

const supportLink = (
  <>
    Please contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
  </>
);

const MSR3EmptyPropsByContentType: Record<
  MSR3EmptyContentType,
  MSR3EmptyContent
> = {
  client: {
    description: supportLink,
    iconProps: { icon: 'person' },
    title: "Looks like you don't have any clients yet.",
  },
  'no-result': {
    description: "We couldn't find what you are looking for.",
    iconProps: { icon: 'search' },
    title: 'No results found',
  },
  payment: {
    iconProps: { icon: 'folderOpen' },
    title: 'No Statements Found',
  },
  title: {
    children: <AddTitleLink />,
    description: "You don't have any titles yet",
    iconProps: { icon: 'plusCircle' },
    title: 'Create your first title',
  },
};

/**
 * Free-form empty state built on the ui-library `Empty` primitive.
 * Spread a preset with `{...MSR3Empty.Props.title}` (or another content key).
 */
const MSR3EmptyRoot = ({
  children,
  className,
  description,
  iconProps,
  title,
}: MSR3EmptyProps) => {
  return (
    <Empty className={className}>
      <EmptyHeader>
        <EmptyMedia>
          <EmptyIcon {...iconProps} />
        </EmptyMedia>

        {title && <EmptyTitle>{title}</EmptyTitle>}

        {description && <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>

      {children && <EmptyContent>{children}</EmptyContent>}
    </Empty>
  );
};

export const MSR3Empty = Object.assign(MSR3EmptyRoot, {
  Props: MSR3EmptyPropsByContentType,
});
